/**
 * PackageCard Component
 * Displays a package with dynamic styling and interactive controls
 */

import React from 'react';
import { Box, Typography, Switch, Button, Chip, Divider } from '@mui/material';
import {
  Zap,
  Crown,
  Shield,
  Package as PackageIcon,
  Star,
  Award,
  Gift,
  Sparkles,
  LucideIcon,
} from 'lucide-react';
import { PackageWithParsedMetadata } from '../../types/admin';
import { formatAdminNumber } from '../../utils/numberFormat';
import { usePackageFeatureCatalog } from '../../services/package.service';

// ============================================
// Icon Mapping
// ============================================

const iconMap: Record<string, LucideIcon> = {
  Zap,
  Crown,
  Shield,
  Package: PackageIcon,
  Star,
  Award,
  Gift,
  Sparkles,
};

// ============================================
// Props Interface
// ============================================

interface PackageCardProps {
  package: PackageWithParsedMetadata;
  onToggleStatus: (publicId: string, isActive: boolean) => void;
  onEdit?: (publicId: string) => void;
}

// ============================================
// PackageCard Component
// ============================================

const featureLimitText = {
  max: 'max',
  perCycle: '/cycle',
  perDay: '/day',
} as const;

/**
 * `dailyLimit` is undefined for features without a per-day limit (plain "(max N)" label) and
 * null when a daily-capable feature has no daily cap set.
 */
const formatFeatureLabel = (name: string, limit?: number | null, dailyLimit?: number | null) => {
  const hasLimit = limit !== undefined && limit !== null;
  if (dailyLimit === undefined) {
    return hasLimit ? `${name} (${featureLimitText.max} ${limit})` : name;
  }
  const parts = [
    hasLimit ? `${limit}${featureLimitText.perCycle}` : null,
    dailyLimit !== null ? `${dailyLimit}${featureLimitText.perDay}` : null,
  ].filter(Boolean);
  return parts.length ? `${name} (${featureLimitText.max} ${parts.join(', ')})` : name;
};

export const PackageCard: React.FC<PackageCardProps> = ({
  package: pkg,
  onToggleStatus,
  onEdit,
}) => {
  const Icon = iconMap[pkg.metadata.icon] || PackageIcon;
  const themeColor = pkg.metadata.color || '#3B82F6';
  const { data: featureCatalog = [] } = usePackageFeatureCatalog();
  const featureNames = new Map(featureCatalog.map((feature) => [feature.code, feature.name]));
  const description = pkg.descriptionEn || pkg.descriptionVi;

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${formatAdminNumber(tokens / 1000000, {
        maximumFractionDigits: 1,
      })}M`;
    }
    if (tokens >= 1000) {
      return `${formatAdminNumber(tokens / 1000, {
        maximumFractionDigits: 1,
      })}K`;
    }
    return formatAdminNumber(tokens);
  };

  // Format duration display (duration is always in days from backend)
  const getDurationText = () => {
    const days = pkg.duration;

    // Convert days to months if divisible by 30
    if (days % 365 === 0) {
      const years = days / 365;
      return `${years} year${years > 1 ? 's' : ''}`;
    }

    if (days % 30 === 0) {
      const months = days / 30;
      return `${months} month${months > 1 ? 's' : ''}`;
    }

    return `${days} day${days > 1 ? 's' : ''}`;
  };

  return (
    <Box
      className="h-full rounded-xl bg-white shadow-sm transition-all hover:shadow-md dark:bg-slate-800"
      sx={{
        borderTop: `4px solid ${themeColor}`,
      }}
    >
      {/* Card Header */}
      <Box className="p-6">
        {/* Icon + Name + Status Toggle */}
        <Box className="mb-4 flex items-start justify-between">
          <Box className="flex items-center gap-3">
            <Box
              className="flex h-12 w-12 items-center justify-center rounded-lg"
              sx={{
                backgroundColor: `${themeColor}20`,
              }}
            >
              <Icon
                size={24}
                style={{
                  color: themeColor,
                }}
              />
            </Box>
            <Box>
              <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
                {pkg.name}
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                Code: {pkg.code}
              </Typography>
              {description && (
                <Typography
                  variant="body2"
                  className="mt-1 line-clamp-2 text-gray-500 dark:text-slate-400"
                >
                  {description}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Status Switch */}
          <Box className="flex flex-col items-end gap-1">
            <Switch
              checked={pkg.isActive}
              onChange={(e) => onToggleStatus(pkg.publicId, e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: themeColor,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: themeColor,
                },
              }}
            />
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {pkg.isActive ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
        </Box>

        {/* Price & Duration */}
        <Box className="mb-4">
          <Box className="flex flex-wrap items-baseline gap-2">
            <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
              ${pkg.price}
            </Typography>
            {pkg.priceVnd !== undefined && pkg.priceVnd > 0 && (
              <Typography
                variant="subtitle1"
                className="font-semibold text-gray-500 dark:text-slate-400"
              >
                / {formatAdminNumber(pkg.priceVnd)} ₫
              </Typography>
            )}
          </Box>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            per {getDurationText()}
          </Typography>
          <Box className="mt-2 flex flex-wrap items-center gap-2">
            <Chip
              label={`Max Agents: ${formatAdminNumber(pkg.maxAgents)}`}
              size="small"
              sx={{
                backgroundColor: '#3B82F620',
                color: '#3B82F6',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${formatTokens(pkg.tokensPerMonth)} credits/cycle`}
              size="small"
              sx={{
                backgroundColor: '#10B98120',
                color: '#10B981',
                fontWeight: 600,
              }}
            />
            {pkg.advanceTokensPerMonth > 0 && (
              <Chip
                label={`${formatTokens(pkg.advanceTokensPerMonth)} advance/cycle`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#10B981',
                  color: '#10B981',
                  fontWeight: 600,
                }}
              />
            )}
            {pkg.allowDynamicTheme && (
              <Chip
                label="Dynamic theme"
                size="small"
                sx={{
                  backgroundColor: '#EC489920',
                  color: '#DB2777',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Box>

        <Divider className="my-4" />

        {/* Token Quota */}
        <Box className="mb-4">
          <Typography
            variant="subtitle2"
            className="mb-3 font-semibold text-gray-700 dark:text-slate-300"
          >
            Package Quota
          </Typography>
          <Box className="space-y-2 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-700/50">
            <Box>
              <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
                {formatAdminNumber(pkg.tokensPerMonth)} tokens
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                per subscription cycle
              </Typography>
            </Box>
            {pkg.advanceTokensPerMonth > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  className="font-medium text-gray-900 dark:text-slate-100"
                >
                  {formatAdminNumber(pkg.advanceTokensPerMonth)} advance tokens
                </Typography>
                <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                  per subscription cycle
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {pkg.features && pkg.features.length > 0 && (
          <Box className="mb-4">
            <Typography
              variant="subtitle2"
              className="mb-2 font-semibold text-gray-700 dark:text-slate-300"
            >
              Features
            </Typography>
            <Box className="flex flex-wrap gap-2">
              {pkg.features.map((featureCode) => (
                <Chip
                  key={featureCode}
                  label={formatFeatureLabel(
                    featureNames.get(featureCode) ?? featureCode,
                    pkg.featureLimits?.[featureCode],
                    pkg.featureDailyLimits?.[featureCode]
                  )}
                  size="small"
                  sx={{
                    backgroundColor: '#7C3AED20',
                    color: '#7C3AED',
                    fontWeight: 600,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {pkg.integrationEntitlements && pkg.integrationEntitlements.length > 0 && (
          <Box className="mb-4">
            <Typography
              variant="subtitle2"
              className="mb-2 font-semibold text-gray-700 dark:text-slate-300"
            >
              Integrations
            </Typography>
            <Box className="flex flex-wrap gap-2">
              {pkg.integrationEntitlements.map((entitlement) => (
                <Chip
                  key={entitlement}
                  label={entitlement.replace('integration.', '')}
                  size="small"
                  sx={{
                    backgroundColor: '#0EA5E920',
                    color: '#0284C7',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Edit Button */}
        {onEdit && (
          <Button
            fullWidth
            variant="outlined"
            onClick={() => onEdit(pkg.publicId)}
            className="transition-all"
            sx={{
              borderColor: themeColor,
              color: themeColor,
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
              '&:hover': {
                borderColor: themeColor,
                backgroundColor: `${themeColor}10`,
              },
            }}
          >
            Edit Package
          </Button>
        )}
      </Box>
    </Box>
  );
};
